package models

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.math.roundToInt

/**
 * The local player's lifetime record, kept on-device only (never synced — it describes whoever
 * plays on this phone). Each finished game folds in once via [record]. Stored as one JSON string,
 * so the history lists are capped to stay well under the smallest platform value limit (Java
 * Preferences allows 8 KB per value on desktop).
 */
@Serializable
data class CareerStats(
    val gamesPlayed: Int = 0,
    val wins: Int = 0,
    // placements[i] = games finished in place i + 1; the last bucket also holds anything lower.
    val placements: List<Int> = emptyList(),
    val totalPoints: Long = 0,
    val bestScore: Int = 0,
    val roundsPlayed: Int = 0,
    val rolls: Int = 0,
    val sevens: Int = 0,
    val doubles: Int = 0,
    val bustsRolled: Int = 0,
    val banks: Int = 0,
    val pointsBanked: Long = 0,
    val bestBank: Int = 0,
    val rollsSurvivedAtBank: Int = 0,
    val timesBusted: Int = 0,
    val pointsLostToBusts: Long = 0,
    val currentWinStreak: Int = 0,
    val bestWinStreak: Int = 0,
    val biggestWinMargin: Int = 0,
    val onlineGames: Int = 0,
    val firstPlayedEpochSeconds: Long = 0,
    val lastPlayedEpochSeconds: Long = 0,
    // Newest first, capped at MAX_RECENT_GAMES.
    val recentGames: List<CareerGame> = emptyList(),
    // Most recently played first, capped at MAX_RIVALS. Bots are never rivals.
    val rivals: List<Rival> = emptyList(),
) {
    val losses: Int get() = gamesPlayed - wins

    val winRatePercent: Int? get() =
        if (gamesPlayed == 0) null else (wins * 100.0 / gamesPlayed).roundToInt()

    val averageScore: Int? get() =
        if (gamesPlayed == 0) null else (totalPoints.toDouble() / gamesPlayed).roundToInt()

    val averageBank: Int? get() =
        if (banks == 0) null else (pointsBanked.toDouble() / banks).roundToInt()

    val averageRollsBeforeBank: Double? get() =
        if (banks == 0) null else rollsSurvivedAtBank.toDouble() / banks

    val style: PlayStyle? get() = PlayStyle.forAverageRolls(averageRollsBeforeBank)

    /** Share of rounds played that ended with this player caught riding a bust. */
    val bustRatePercent: Int? get() =
        if (roundsPlayed == 0) null else (timesBusted * 100.0 / roundsPlayed).roundToInt()

    /** Sevens per roll against the 1-in-6 a fair pair of dice gives, as signed percentage points. */
    val sevenLuckPoints: Int? get() = luckPoints(sevens)

    /** Doubles per roll against the fair 1-in-6, as signed percentage points. */
    val doublesLuckPoints: Int? get() = luckPoints(doubles)

    /** Rivals with the most shared games first; ties go to whoever was played more recently. */
    val topRivals: List<Rival> get() =
        rivals.sortedWith(compareByDescending<Rival> { it.games }.thenByDescending { it.lastPlayedEpochSeconds })

    private fun luckPoints(count: Int): Int? {
        if (rolls < MIN_ROLLS_FOR_LUCK) return null
        return ((count.toDouble() / rolls - 1.0 / 6.0) * 100).roundToInt()
    }

    companion object {
        const val MAX_RECENT_GAMES = 30
        const val MAX_RIVALS = 24
        const val MAX_PLACEMENT_BUCKETS = 8

        // Below this many rolls a luck read is noise, so it isn't shown.
        const val MIN_ROLLS_FOR_LUCK = 30
    }
}

// The list entries use one/two-letter keys: they repeat up to 30 times, and the whole career has to
// fit one stored preference value.
@Serializable
data class CareerGame(
    @SerialName("t") val playedAtEpochSeconds: Long = 0,
    @SerialName("p") val placement: Int = 0,
    @SerialName("n") val playerCount: Int = 0,
    @SerialName("s") val score: Int = 0,
    // For a loss, who won and with what; for a win, the runner-up's score (for the margin).
    @SerialName("w") val winnerName: String = "",
    @SerialName("ws") val winnerScore: Int = 0,
    @SerialName("rs") val runnerUpScore: Int = 0,
    @SerialName("r") val rounds: Int = 0,
    @SerialName("o") val online: Boolean = false,
) {
    val won: Boolean get() = placement == 1
}

@Serializable
data class Rival(
    @SerialName("n") val name: String = "",
    @SerialName("g") val games: Int = 0,
    // Games where you finished ahead of them / they finished ahead of you. Ties count as neither.
    @SerialName("w") val winsAgainst: Int = 0,
    @SerialName("l") val lossesAgainst: Int = 0,
    @SerialName("t") val lastPlayedEpochSeconds: Long = 0,
)

/** A finished game, seen from the local player's seat — the input to [CareerStats.record]. */
data class CompletedGame(
    val selfName: String,
    val standings: List<FinalStanding>,
    val rounds: Int,
    val online: Boolean,
    val selfStats: PlayerGameStats,
    val playedAtEpochSeconds: Long,
) {
    data class FinalStanding(val name: String, val points: Int, val isBot: Boolean)

    companion object {
        /**
         * Snapshot [game] for [selfName], or null when it shouldn't count toward a career: the game
         * isn't over, it ended early because everyone else left, or [selfName] isn't at the table.
         */
        fun from(
            game: DomainGame,
            selfName: String,
            online: Boolean,
            playedAtEpochSeconds: Long,
        ): CompletedGame? {
            val round = game.round ?: return null
            if (game.endRoundNum < 1 || round.roundNum <= game.endRoundNum) return null
            if (game.players.size < 2 || game.players.none { it.name == selfName }) return null
            return CompletedGame(
                selfName = selfName,
                standings = game.players.map { FinalStanding(it.name, it.points, it.isBot) },
                rounds = game.endRoundNum,
                online = online,
                selfStats = game.recapStats.playerStats.firstOrNull { it.playerName == selfName }
                    ?: PlayerGameStats(playerName = selfName),
                playedAtEpochSeconds = playedAtEpochSeconds,
            )
        }
    }
}

/** Folds one finished game into the career. */
fun CareerStats.record(game: CompletedGame): CareerStats {
    val self = game.standings.firstOrNull { it.name == game.selfName } ?: return this
    val others = game.standings.filter { it.name != game.selfName }
    val placement = 1 + others.count { it.points > self.points }
    val won = placement == 1
    val bestOther = others.maxOfOrNull { it.points } ?: 0
    val winner = game.standings.maxByOrNull { it.points }
    val stats = game.selfStats

    val bucketIndex = (placement - 1).coerceIn(0, CareerStats.MAX_PLACEMENT_BUCKETS - 1)
    val updatedPlacements = List(maxOf(placements.size, bucketIndex + 1)) { index ->
        placements.getOrElse(index) { 0 } + if (index == bucketIndex) 1 else 0
    }
    val streak = if (won) currentWinStreak + 1 else 0

    val record = CareerGame(
        playedAtEpochSeconds = game.playedAtEpochSeconds,
        placement = placement,
        playerCount = game.standings.size,
        score = self.points,
        winnerName = if (won) game.selfName else winner?.name.orEmpty(),
        winnerScore = winner?.points ?: self.points,
        runnerUpScore = if (won) bestOther else self.points,
        rounds = game.rounds,
        online = game.online,
    )

    return copy(
        gamesPlayed = gamesPlayed + 1,
        wins = wins + if (won) 1 else 0,
        placements = updatedPlacements,
        totalPoints = totalPoints + self.points,
        bestScore = maxOf(bestScore, self.points),
        roundsPlayed = roundsPlayed + game.rounds,
        rolls = rolls + stats.rolls,
        sevens = sevens + stats.sevens,
        doubles = doubles + stats.doubles,
        bustsRolled = bustsRolled + stats.bustsRolled,
        banks = banks + stats.banks,
        pointsBanked = pointsBanked + stats.pointsBanked,
        bestBank = maxOf(bestBank, stats.bestBank),
        rollsSurvivedAtBank = rollsSurvivedAtBank + stats.rollsSurvivedAtBank,
        timesBusted = timesBusted + stats.timesBusted,
        pointsLostToBusts = pointsLostToBusts + stats.pointsLostToBusts,
        currentWinStreak = streak,
        bestWinStreak = maxOf(bestWinStreak, streak),
        biggestWinMargin = if (won) maxOf(biggestWinMargin, self.points - bestOther) else biggestWinMargin,
        onlineGames = onlineGames + if (game.online) 1 else 0,
        firstPlayedEpochSeconds = if (firstPlayedEpochSeconds == 0L) game.playedAtEpochSeconds else firstPlayedEpochSeconds,
        lastPlayedEpochSeconds = maxOf(lastPlayedEpochSeconds, game.playedAtEpochSeconds),
        recentGames = (listOf(record) + recentGames).take(CareerStats.MAX_RECENT_GAMES),
        rivals = updatedRivals(rivals, self, others.filterNot { it.isBot }, game.playedAtEpochSeconds),
    )
}

private fun updatedRivals(
    rivals: List<Rival>,
    self: CompletedGame.FinalStanding,
    opponents: List<CompletedGame.FinalStanding>,
    playedAt: Long,
): List<Rival> {
    val touched = opponents.map { opponent ->
        // Names are how Banko identifies people, but players retype them — match case-insensitively.
        val existing = rivals.firstOrNull { it.name.equals(opponent.name, ignoreCase = true) }
            ?: Rival(name = opponent.name)
        existing.copy(
            name = opponent.name,
            games = existing.games + 1,
            winsAgainst = existing.winsAgainst + if (self.points > opponent.points) 1 else 0,
            lossesAgainst = existing.lossesAgainst + if (opponent.points > self.points) 1 else 0,
            lastPlayedEpochSeconds = playedAt,
        )
    }
    val untouched = rivals.filterNot { rival ->
        opponents.any { it.name.equals(rival.name, ignoreCase = true) }
    }
    return (touched + untouched).take(CareerStats.MAX_RIVALS)
}

/** Lenient JSON for the stored career: unknown fields from newer builds are ignored, corrupt data resets. */
object CareerStatsCodec {
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = false
    }

    fun decode(value: String?): CareerStats {
        if (value.isNullOrBlank()) return CareerStats()
        return try {
            json.decodeFromString(CareerStats.serializer(), value)
        } catch (_: Exception) {
            CareerStats()
        }
    }

    fun encode(stats: CareerStats): String = json.encodeToString(CareerStats.serializer(), stats)
}
